import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../shared/components/Icon';
import { useDockDrag } from './dockDrag';
import type { DropZone } from './dockModel';
import { useDockElements } from './WorkspaceProvider';

const compassZones: readonly DropZone[] = ['center', 'top', 'bottom', 'start', 'end'];

/**
 * What the workspace shows while something is being dragged.
 *
 * Three layers, in the order they answer the question "where will this land?": the
 * shaded rectangle the document would occupy, the compass over the pane under the
 * pointer showing the four splits and the tab strip it could join instead, and a small
 * card at the pointer naming the document being carried.
 */
export function DockOverlay() {
  const { t } = useTranslation();
  const { drag } = useDockDrag();
  const { frames } = useDockElements();
  if (drag === null) return null;

  const { resolution, pointer, subject } = drag;
  const hostFrame = resolution?.hostId == null ? undefined : frames.get(resolution.hostId);
  const compass = hostFrame?.getBoundingClientRect();
  const rtl = document.documentElement.dir === 'rtl';

  const hint = resolution === null
    ? t('workspace.dropNowhere')
    : resolution.hint === 'tab'
      ? t('workspace.dropAsTab')
      : resolution.hint === 'split'
        ? t('workspace.dropAsSplit')
        : resolution.hint === 'edge'
          ? t('workspace.dropAtEdge')
          : t('workspace.dropAsWindow');

  // A window already shows where it is going by being there, so nothing is painted over it.
  const moving = subject.kind === 'window' && resolution?.hint === 'float';

  return createPortal(
    <div className="dock-overlay" aria-hidden="true">
      {resolution === null || moving ? null : (
        <div
          className={`dock-overlay__preview dock-overlay__preview--${resolution.hint}`}
          style={{
            left: `${resolution.preview.x}px`,
            top: `${resolution.preview.y}px`,
            width: `${resolution.preview.width}px`,
            height: `${resolution.preview.height}px`,
          }}
        />
      )}

      {compass === undefined || resolution?.hint === 'edge' ? null : (
        <div
          className="dock-compass"
          style={{ left: `${compass.left + compass.width / 2}px`, top: `${compass.top + compass.height / 2}px` }}
        >
          {compassZones.map((zone) => (
            <span
              key={zone}
              className={`dock-compass__cell dock-compass__cell--${physicalClass(zone, rtl)}${resolution?.zone === zone ? ' dock-compass__cell--on' : ''}`}
            />
          ))}
        </div>
      )}

      {moving ? null : (
        <div className="dock-ghost" style={{ left: `${pointer.x}px`, top: `${pointer.y}px` }}>
          <Icon name={subject.icon} className="icon icon--small" />
          <span>{subject.label}</span>
          <em>{hint}</em>
        </div>
      )}
    </div>,
    document.body,
  );
}

/** The compass is drawn in screen terms, so a logical zone has to pick a physical cell. */
function physicalClass(zone: DropZone, rtl: boolean): string {
  if (zone === 'center' || zone === 'top' || zone === 'bottom') return zone;
  const isLeft = zone === 'start' ? !rtl : rtl;
  return isLeft ? 'left' : 'right';
}
